import * as XLSX from "xlsx";
import { v4 as uuidv4 } from "uuid";
import { TaskTemplateWithIdType } from "../types/TaskTemplateType";

export type ExcelValidationError = {
  row: number;
  column: string;
  message: string;
};

export type ParseExcelResult = {
  success: boolean;
  data: TaskTemplateWithIdType[];
  errors: ExcelValidationError[];
};

type QuestionTypeOption = {
  id: string;
  type: string;
};

const QUESTION_TYPE_MAP: Record<string, string> = {
  "free text": "Free Text",
  "single select list": "Single Select List",
  "multi-select list": "Multi-select List",
  "check boxes": "Check Boxes",
  "radio buttons": "Radio Buttons",
};

const resolveQuestionTypeId = (
  typeText: string,
  questionsType: QuestionTypeOption[]
): number | null => {
  const normalized = typeText.trim().toLowerCase();
  const match = questionsType.find(
    (qt) => qt.type.trim().toLowerCase() === normalized
  );
  return match ? parseInt(match.id) : null;
};

const isSelectionType = (typeText: string): boolean => {
  const normalized = typeText.trim().toLowerCase();
  return (
    normalized === "single select list" ||
    normalized === "multi-select list" ||
    normalized === "check boxes" ||
    normalized === "radio buttons"
  );
};

export const parseTaskExcelTemplate = (
  file: File,
  questionsType: QuestionTypeOption[]
): Promise<ParseExcelResult> => {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const data = e.target?.result;
      if (!data) {
        resolve({
          success: false,
          data: [],
          errors: [{ row: 0, column: "", message: "Failed to read file" }],
        });
        return;
      }

      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const rows: any[] = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      if (rows.length === 0) {
        resolve({
          success: false,
          data: [],
          errors: [{ row: 0, column: "", message: "Excel file has no data rows" }],
        });
        return;
      }

      const errors: ExcelValidationError[] = [];
      const parsed: TaskTemplateWithIdType[] = [];

      rows.forEach((row, index) => {
        const rowNum = index + 2; // Excel row (1-indexed header + 1-indexed data)
        const question = (row["Question"] ?? "").toString().trim();
        const questionType = (row["Question Type"] ?? "").toString().trim();
        const description = (row["Description"] ?? "").toString().trim();
        const choices = (row["Choices"] ?? "").toString().trim();
        const isRequiredRaw = (row["is Required"] ?? "").toString().trim();

        // Validate Question
        if (!question) {
          errors.push({ row: rowNum, column: "Question", message: "Question must not be empty" });
        }

        // Validate Question Type
        if (!questionType) {
          errors.push({ row: rowNum, column: "Question Type", message: "Question Type must not be empty" });
        } else {
          const normalized = questionType.toLowerCase();
          if (!QUESTION_TYPE_MAP[normalized]) {
            errors.push({
              row: rowNum,
              column: "Question Type",
              message: `Question Type must be one of: Free Text, Single Select List, Multi-select List, Check Boxes, Radio Buttons. Got: "${questionType}"`,
            });
          }

          const typeId = resolveQuestionTypeId(questionType, questionsType);
          if (typeId === null && QUESTION_TYPE_MAP[normalized]) {
            errors.push({
              row: rowNum,
              column: "Question Type",
              message: `Question Type "${questionType}" is not available in the system`,
            });
          }
        }

        // Validate Choices
        if (isSelectionType(questionType) && !choices) {
          errors.push({
            row: rowNum,
            column: "Choices",
            message: "Choices are required for Single Select / Multi Select questions",
          });
        }
        if (!isSelectionType(questionType) && questionType && choices) {
          errors.push({
            row: rowNum,
            column: "Choices",
            message: "Choices must not be populated for Free Text questions",
          });
        }

        // Validate is Required
        const isRequiredNorm = isRequiredRaw.toLowerCase();
        if (isRequiredNorm !== "yes" && isRequiredNorm !== "no") {
          errors.push({
            row: rowNum,
            column: "is Required",
            message: `is Required must be "Yes" or "No". Got: "${isRequiredRaw}"`,
          });
        }

        // Build parsed item regardless (only used if no errors)
        const typeId = resolveQuestionTypeId(questionType, questionsType) ?? 1;
        parsed.push({
          id: uuidv4(),
          question,
          questionTypeId: typeId,
          questionDescription: description,
          questionChoices: choices,
          isRequired: isRequiredNorm === "yes",
        });
      });

      resolve({
        success: errors.length === 0,
        data: errors.length === 0 ? parsed : [],
        errors,
      });
    };

    reader.readAsArrayBuffer(file);
  });
};
